import React, { useEffect, useRef } from 'react';
import KeyBadge from '../create/keys/KeyBadge';
import { TUTORIAL_TARGETS, tutorialAttr } from '../../../tutorials';
import styles from './styles/PreviewKeysPrompt.module.css';

const COPY = {
    restart: {
        title: 'Restart preview',
        clear: 'Clear keys and restart',
        keep: 'Keep keys and restart',
    },
    exit: {
        title: 'Exit preview',
        clear: 'Clear keys and exit',
        keep: 'Keep keys and exit',
    },
};

/**
 * Asked when an author restarts or leaves a preview while holding keys.
 *
 * Keeping them lets an author jump straight to testing a later part of the
 * scenario without replaying the route that hands the keys out. Players are
 * never asked: a real restart always clears.
 *
 * @param {{
 *   action: 'restart'|'exit',
 *   keyNames: string[],
 *   onClear: () => void,
 *   onKeep: () => void,
 *   onCancel: () => void,
 * }} props
 */
const PreviewKeysPrompt = ({ action, keyNames = [], onClear, onKeep, onCancel }) => {
    const copy = COPY[action] || COPY.restart;
    const clearRef = useRef(null);

    useEffect(() => {
        if (clearRef.current) clearRef.current.focus();
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onCancel();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onCancel]);

    const count = keyNames.length;

    return (
        <div
            className={styles.overlay}
            onClick={(event) => {
                if (event.target === event.currentTarget) onCancel();
            }}
        >
            <div
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="preview-keys-prompt-title"
                {...tutorialAttr(TUTORIAL_TARGETS.PREVIEW_KEYS_PROMPT)}
            >
                <h2 id="preview-keys-prompt-title" className={styles.title}>
                    {copy.title}
                </h2>
                <p className={styles.description}>
                    You are holding {count === 1 ? 'a key' : `${count} keys`}. Clear{' '}
                    {count === 1 ? 'it' : 'them'} to test from a fresh start, or keep{' '}
                    {count === 1 ? 'it' : 'them'} to test parts of the scenario that need{' '}
                    {count === 1 ? 'it' : 'them'}.
                </p>
                <ul className={styles.keys}>
                    {keyNames.map((name, index) => (
                        // Names are not unique; position is stable for this dialog.
                        <li key={`${name}-${index}`}>
                            <KeyBadge role="grant" size="sm" name={name} />
                        </li>
                    ))}
                </ul>
                <p className={styles.note}>Only previews ask. Players always start over with no keys.</p>
                <div className={styles.actions}>
                    <button
                        ref={clearRef}
                        type="button"
                        className={`${styles.button} ${styles.primary}`}
                        onClick={onClear}
                    >
                        {copy.clear}
                    </button>
                    <button type="button" className={`${styles.button} ${styles.secondary}`} onClick={onKeep}>
                        {copy.keep}
                    </button>
                    <button type="button" className={`${styles.button} ${styles.cancel}`} onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PreviewKeysPrompt;
